import { Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from "rxjs";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Submission } from "../../models/submission.model";
import { GlobalService } from "../../../global/services/global.service";
import { WorkspaceService } from "../../../workspace/workspace.service";
import {
  firstGridRowObjectFromPayload,
  rowPayloadFromActiveScreen,
} from "../../../shared/workspace-element-context";
import { EFileService } from "../../efile.service";
import { GtwSnackbarService } from "../../../shared/_services/gtw-snackbar.service";
import { AppService } from "../../../app.service";
import { applySubmissionSidebarHidden } from "./submission-sidebar-layout.util";

interface SidebarMenuItem {
  /** Stable id used for highlight tracking (matches `menuFromSubState` output). */
  menuKey: string;
  /** Visible label (matches legacy ui-router state name in submission.html). */
  label: string;
  /**
   * Sub-state to push into the URL. Maps 1:1 to a legacy
   * `submissions-dasboard.submission.<state>` (see ui/app/components/efile/efile.js).
   * Omitted for action-only entries (e.g. PDF downloads).
   */
  subState?: string;
  /**
   * Full URL segments (relative to `/submissions-dasboard/submission/:id`).
   * For most leaf states this is `[subState]`. For the legacy nested state
   * `submissions-dasboard.submission.data.addEdit` (efile.js L149), the URL
   * is `/data/add-edit`, so `urlSegments = ['data', 'add-edit']`. Keeping
   * the parent segment lets the breadcrumb walk the full hierarchy.
   */
  urlSegments?: string[];
  /**
   * Optional parent state label rendered in the breadcrumb ahead of the
   * page label. Mirrors the legacy ui-router `data` parent ("Data" →
   * "Add / Edit") so the breadcrumb tracks the full state path.
   */
  parentLabel?: string;
  /** Non-routing action for sidebar-driven side-effects (PDF downloads). */
  action?: 'download-8453' | 'download-8879';
  /**
   * Optional visibility predicate — mirrors legacy `ng-if` + `if-has-access`
   * conditions from `submission.html`. Item is shown when this returns `true`
   * (or when absent, always shown).
   */
  condition?: () => boolean;
}

/** Payload emitted by the sidebar so the parent can drive Router.navigate. */
export interface SubmissionSubStateNav {
  subState: string;
  /** Path segments relative to `/submissions-dasboard/submission/:id`. */
  urlSegments: string[];
}

/**
 * Friendly labels for Pattern B child segments (parent-state-internal tabs).
 * Used in the breadcrumb. Keys match the URL slug; values mirror the legacy
 * tab labels in `efile-scrubbing.html` / `return-review.html`.
 */
const CHILD_SUBSTATE_LABELS: Record<string, string> = {
  // efile-scrubbing.* (efile.js L228/L382/L508)
  list: 'Scrubbing list',
  run: 'Run Scrubbing',
  history: 'Scrubbing History',
  // return-review.* (efile.js L621-L1101) — legacy step labels.
  errors: 'Errors',
  'efile-diagnostics': 'Efile Diagnostics',
  'efile-sno-dups': 'Sno Duplicates',
  'submission-errors': 'Submission Errors',
  'total-review-diag': 'Total Review Diagnostics',
  'amounts-reconciliation': 'Amounts Reconciliation',
  'py-cy': 'PY/CY',
  'efile-sub-scrubbings': 'Efile Sub Scrubbings',
  'xml-vs-elements': 'XML vs Elements',
  'xml-vs-elements-counts': 'XML vs Elements Counts',
};

/**
 * Port of legacy ui-router state `submissions-dasboard.submission`
 * (`ui/app/components/efile/efile.js` L39). Renders the submission
 * sidebar + the currently-selected child state inline.
 *
 * Previously this component relied on a nested `<router-outlet>` to
 * render each child state, but the outlet sits inside a `mat-tab-body`
 * which only mounts after the tab is active — so the initial router
 * resolution silently dropped the child component and fell back to the
 * top-level `/dashboard` route. `SubmissionDataComponent` now supplies
 * `[subState]` (parsed from the URL) and this template renders the
 * matching screen via `ngSwitch`, which matches legacy behavior where
 * `ui-view` would swap content inside the submission shell.
 */
@Component({
    selector: 'submission-detail',
    templateUrl: './submission-detail.component.html',
    styleUrls: ['./submission-detail.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SubmissionDetailComponent implements OnInit, OnDestroy, OnChanges {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  /**
   * Submission row passed down by `SubmissionDataComponent` when a detail
   * tab is activated. Also hydrated as a fallback from
   * `WorkspaceService.activeScreen` when invoked from the legacy shell.
   */
  @Input() submission: Submission = {} as Submission;

  /** Parent-supplied tax year (see submission-data.component.html binding). */
  @Input() taxYear: string = '2020';

  /**
   * Federal vs state — mirrors `SubmissionDataComponent.submissionType`
   * (list filter) and legacy `vm.submissionType` on overrides grid params.
   */
  @Input() submissionType: string = 'federal';

  /**
   * Active child state (`dashboard`, `submit`, `return-review`, ...).
   * Driven by `SubmissionDataComponent.activeSubState`, which reads it
   * from the `:subState` URL segment. Used by the template's ngSwitch
   * to pick which legacy screen to render inline.
   */
  @Input() subState: string = 'dashboard';

  /**
   * Pattern B (parent-state-internal) child key — populated when the
   * URL has a nested segment that the parent component owns
   * internally (e.g. `/efile-scrubbing/list`, `/return-review/efile-diagnostics`).
   * Forwarded to the relevant child components via `[childSubState]` so they
   * can pre-select their internal tab/step. Empty for flat Pattern-A URLs.
   */
  @Input() childSubState: string = '';

  /**
   * Sidebar clicks bubble a `{subState, urlSegments}` payload up so the
   * parent can push the matching legacy URL. Most items stay flat
   * (`/submissions-dasboard/submission/:id/<sub>`), but the legacy
   * `data.addEdit` child uses `/data/add-edit` (efile.js L149), which we
   * preserve via the `urlSegments` array.
   *
   * Backward compat: a plain string is still accepted on the parent side
   * (`SubmissionDataComponent.handleSubStateChange`) for deep-link / API
   * callers that only know the canonical sub-state key.
   */
  @Output() subStateChange = new EventEmitter<SubmissionSubStateNav | string>();

  selectedMenuItem: string = 'dashboard';

  addEditSubmissionMenuOpen = false;

  listName: any;

  /** Inline spinner state for the sidebar download buttons. */
  downloading8453 = false;
  downloading8879 = false;

  /**
   * All sidebar menu items in legacy order (`submission.html` L204-229).
   * Visibility is controlled per-item via the `condition` predicate which
   * mirrors the original `ng-if` + `if-has-access` directives. Use the
   * `visibleMenuItems` getter to obtain the filtered list for rendering.
   */
  readonly menuItems: SidebarMenuItem[] = [
    // No condition — always visible (no ng-if / if-has-access in legacy).
    { menuKey: 'dashboard',           label: 'Dashboard',                subState: 'dashboard',               urlSegments: ['dashboard'] },
    // if-has-access="EDITOR" (user_type_key >= 2, EfileService.js L29/L136).
    { menuKey: 'add-edit',            label: 'Add / Edit',               subState: 'add-edit',                urlSegments: ['data', 'add-edit'], parentLabel: 'Data',
      condition: () => this.hasEfileAccess('EDITOR') },
    { menuKey: 'overrides',           label: 'List of Overrides',        subState: 'overrides',               urlSegments: ['overrides'] },
    { menuKey: 'pdf-upload',          label: 'PDF Upload',               subState: 'pdf-upload',              urlSegments: ['pdf-upload'] },
    // if-has-access="EDITOR" && SUBMISSION_STATUS !== PRODUCTION_ACCEPTED && !enableReviewProcess
    { menuKey: 'validation',          label: 'Validation',               subState: 'validation',              urlSegments: ['validation'],
      condition: () => this.hasEfileAccess('EDITOR') && !this.isProductionAccepted && !this.enableReviewProcess },
    // ng-if="enableReviewProcess"
    // Legacy ui-sref: submissions-dasboard.submission.return-review.dashboard
    { menuKey: 'return-review',       label: 'Return Review',            subState: 'return-review',           urlSegments: ['return-review', 'dashboard'],
      condition: () => this.enableReviewProcess },
    // if-has-access="EDITOR" && SUBMISSION_STATUS !== PRODUCTION_ACCEPTED
    { menuKey: 'submit',              label: 'Send Return to IRS',       subState: 'submit',                  urlSegments: ['submit'],
      condition: () => this.hasEfileAccess('EDITOR') && !this.isProductionAccepted },
    { menuKey: 'xml-upload',          label: 'Upload Consol XML',        subState: 'xml-upload',              urlSegments: ['xml-upload'] },
    { menuKey: 'download-8453',       label: 'Download 8453 Pdf',        action: 'download-8453' },
    { menuKey: 'download-8879',       label: 'Download 8879 Pdf',        action: 'download-8879' },
    // if-has-access="ADMIN" && SUBMISSION_STATUS !== PRODUCTION_ACCEPTED
    { menuKey: 'delete-form',         label: 'Delete Forms',             subState: 'delete-form',             urlSegments: ['delete-form'],
      condition: () => this.hasEfileAccess('ADMIN') && !this.isProductionAccepted },
    { menuKey: 'lock-unlock',         label: 'Lock/Unlock',              subState: 'lock-unlock',             urlSegments: ['lock-unlock'],
      condition: () => this.hasEfileAccess('ADMIN') && !this.isProductionAccepted },
    // if-has-access="ADMIN" && SUBMISSION_STATUS === PRODUCTION_ACCEPTED && canDoProdSubmit
    { menuKey: 'xml-sync',            label: 'XML Sync with SLT',        subState: 'xml-sync',                urlSegments: ['xml-sync'],
      condition: () => this.hasEfileAccess('ADMIN') && this.isProductionAccepted && this.canDoProdSubmit },
    // if-has-access="ADMIN" && !IS_LOCKED
    { menuKey: 'enabled-disabled',    label: 'Enabled/Disabled Forms',   subState: 'enabled-disabled',        urlSegments: ['enabled-disabled'],
      condition: () => this.hasEfileAccess('ADMIN') && !this.submission?.IS_LOCKED },
    // if-has-access="ADMIN" && SUBMISSION_STATUS !== PRODUCTION_ACCEPTED
    { menuKey: 'unlock-validation',   label: 'Unlock Validation',        subState: 'unlock-validation',       urlSegments: ['unlock-validation'],
      condition: () => this.hasEfileAccess('ADMIN') && !this.isProductionAccepted },
    { menuKey: 'formelements-report', label: 'Form/Elements Report',     subState: 'formelements-report',     urlSegments: ['formelements-report'] },
    // if-has-access="ADMIN"
    { menuKey: 'amend-superseded',    label: 'Amend/Superseded Returns', subState: 'amendsuperseded-returns', urlSegments: ['amendsuperseded-returns'],
      condition: () => this.hasEfileAccess('ADMIN') },
    // if-has-access="ADMIN" && SUBMISSION_STATUS !== PRODUCTION_ACCEPTED
    { menuKey: 'efile-scrubbing',     label: 'Efile Scrubbing',          subState: 'efile-scrubbing',         urlSegments: ['efile-scrubbing'],
      condition: () => this.hasEfileAccess('ADMIN') && !this.isProductionAccepted },
    // if-has-access="ADMIN"
    { menuKey: 'key-xml-gen',         label: 'Xml Generation',           subState: 'key-xml-gen',             urlSegments: ['key-xml-gen'],
      condition: () => this.hasEfileAccess('ADMIN') },
    { menuKey: 'xml-compare',         label: 'Xml Comparison',           subState: 'xml-compare',             urlSegments: ['xml-compare'] },
  ];

  /**
   * Filtered view of `menuItems` — only those whose `condition` predicate
   * passes (or have no predicate). Mirrors legacy `ng-if` + `if-has-access`
   * visibility in `submission.html` L204-229.
   */
  get visibleMenuItems(): SidebarMenuItem[] {
    return this.menuItems.filter(item => !item.condition || item.condition());
  }

  // ---------------------------------------------------------------------------
  // Legacy constants (EfileElementsController.js L443)
  // ---------------------------------------------------------------------------
  private readonly PRODUCTION_ACCEPTED = 'Production Accepted';

  /**
   * Role → numeric threshold map, mirroring `EfileService.js` L27-L32.
   * `hasAccess(req)` returns true when `user_type_key >= roleMap[req]`.
   */
  private readonly ROLE_MAP: Record<string, number> = {
    USER: 1, EDITOR: 2, ADMIN: 3, DEVELOPER: 4, SUPER_ADMIN: 5,
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private efileService: EFileService,
    private snackbar: GtwSnackbarService,
    private appService: AppService,
    private readonly elRef: ElementRef<HTMLElement>,
  ) {}

  // ---------------------------------------------------------------------------
  // Visibility helpers — mirror `EfileFactory.hasAccess` and submission flags
  // ---------------------------------------------------------------------------

  /**
   * Mirrors `EfileFactory.hasAccess(requiredAccess)` from `EfileService.js` L136:
   * `USER_SETTINGS.user.client.user_type_key >= userMap[requiredAccess]`.
   */
  private hasEfileAccess(role: 'USER' | 'EDITOR' | 'ADMIN' | 'DEVELOPER' | 'SUPER_ADMIN'): boolean {
    const typeKey: number = this.appService.USER_SETTINGS?.user?.client?.user_type_key ?? 0;
    return typeKey >= (this.ROLE_MAP[role] ?? 99);
  }

  /** True when the submission has been Production Accepted. */
  private get isProductionAccepted(): boolean {
    return (this.submission?.SUBMISSION_STATUS || '') === this.PRODUCTION_ACCEPTED;
  }

  private get ssoId(): string {
    return this.appService.USER_SETTINGS?.user?.sso_id ?? '';
  }

  /**
   * Mirrors `vm.enableReviewProcess` (EfileElementsController.js L446):
   * True when the current SSO ID is included in `SERVER_CONFIG.configSettings.enable_review_process`
   * or that setting equals the string `'all'`.
   */
  private get enableReviewProcess(): boolean {
    const setting: string = this.appService.SERVER_CONFIG?.configSettings?.enable_review_process ?? '';
    if (!setting) return false;
    return setting.includes(this.ssoId) || setting.toLowerCase() === 'all';
  }

  /**
   * Mirrors `vm.canDoProdSubmit` (EfileElementsController.js L444):
   * True when the current SSO ID is in `SERVER_CONFIG.clientConfig.efile_prod_submission_users`.
   */
  private get canDoProdSubmit(): boolean {
    const users: string = this.appService.SERVER_CONFIG?.clientConfig?.efile_prod_submission_users ?? '';
    if (!users || !this.ssoId) return false;
    return users.includes(this.ssoId);
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
    this.selectedMenuItem = this.menuFromSubState(this.subState);
    this.syncShellSidebarForSubState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subState']) {
      this.selectedMenuItem = this.menuFromSubState(this.subState);
      this.syncShellSidebarForSubState();
    }
  }

  ngOnDestroy(): void {
    applySubmissionSidebarHidden(false, this.elRef.nativeElement);
    this.subs.unsubscribe();
  }

  /**
   * Backfill `submission` / `taxYear` from the legacy WorkspaceService when
   * the component is mounted outside of the Angular `SubmissionDataComponent`
   * (e.g. from legacy AngularJS). When the parent binds these as @Inputs
   * (the default Angular 12 path), those values win because we only
   * overwrite if they haven't been supplied.
   */
  private applyWorkspaceContext(): void {
    if (this.submission && (this.submission as any).SUBMISSION_ID) return;

    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const parsed = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    if (parsed && Object.keys(parsed).length > 0) {
      this.submission = parsed as Submission;
      if ((parsed as any).TAX_YEAR) {
        this.taxYear = String((parsed as any).TAX_YEAR);
      }
    }
  }

  /**
   * Add/Edit (`gtw-efile-add-edit`) uses full width by default: hide the
   * submission shell `#sidebar-container`. The add/edit aside chevron calls
   * `applySubmissionSidebarHidden(false)` to bring it back (see add-edit.component.ts).
   */
  private isAddEditSubState(): boolean {
    const s = (this.subState || "").toLowerCase();
    return s === "add-edit" || s === "efile-data";
  }

  private syncShellSidebarForSubState(): void {
    if (!this.isAddEditSubState()) {
      this.addEditSubmissionMenuOpen = false;
    }
    applySubmissionSidebarHidden(this.isAddEditSubState(), this.elRef.nativeElement);
  }

  onAddEditSubmissionMenuOpenChange(open: boolean): void {
    this.addEditSubmissionMenuOpen = !!open;
  }

  /**
   * Sidebar click dispatcher — either bubbles a sub-state change up to the
   * parent (router push happens there) or invokes a side-effect action
   * such as the legacy 8453/8879 PDF download buttons.
   */
  onMenuClick(item: SidebarMenuItem): void {
    if (item.action === 'download-8453') {
      this.handleDownload8453();
      return;
    }
    if (item.action === 'download-8879') {
      this.handleDownload8879();
      return;
    }
    if (item.subState) {
      if (item.menuKey === 'add-edit') {
        this.addEditSubmissionMenuOpen = false;
      }
      this.selectedMenuItem = item.menuKey;

      const urlSegments = item.urlSegments && item.urlSegments.length > 0
        ? item.urlSegments
        : [item.subState];

      // Emit the structured payload so the Angular host
      // (`SubmissionDataComponent.handleSubStateChange`) can update its
      // local state caches (`activeSubState`, `activeUrlSegments`,
      // breadcrumb labels) before the URL change triggers
      // `capturePendingDeepLink` again.
      this.subStateChange.emit({
        subState: item.subState,
        urlSegments,
      });
    }
  }

  isSelected(menuKey: string): boolean {
    if (menuKey === 'add-edit' && this.isAddEditSubState() && this.addEditSubmissionMenuOpen) {
      return false;
    }
    return this.selectedMenuItem === menuKey;
  }

  /**
   * Sidebar item currently rendered in the right pane. Used by the breadcrumb
   * to surface the active screen name (and its parent state, when nested).
   * Action-only items (PDF downloads) never appear here because they don't
   * change the displayed sub-state.
   */
  get currentMenuItem(): SidebarMenuItem | undefined {
    return this.menuItems.find(
      (item) => !item.action && item.menuKey === this.selectedMenuItem,
    );
  }

  /**
   * Visible label for the current page (right-most breadcrumb segment).
   * Falls back to "Dashboard" so the breadcrumb never goes blank.
   */
  get currentMenuLabel(): string {
    return this.currentMenuItem?.label || 'Dashboard';
  }

  /**
   * Optional parent state label rendered between the tax payer and the
   * page label when the active state is nested (e.g. legacy
   * `data.addEdit` shows "... > Data > Add / Edit").
   */
  get currentParentLabel(): string {
    return this.currentMenuItem?.parentLabel || '';
  }

  /**
   * Humanized label for the Pattern B internal child segment
   * (e.g. `efile-scrubbing/list` → "List"). Rendered after the page label
   * so the breadcrumb walks the full URL like legacy
   * `submissions-dasboard.submission.efileScrubbing.list` did.
   */
  get childSubStateLabel(): string {
    if (!this.childSubState) return '';
    return CHILD_SUBSTATE_LABELS[this.childSubState] ||
      this.titleCase(this.childSubState);
  }

  private titleCase(slug: string): string {
    return slug
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  /**
   * Map a sub-state key (URL segment) back to the matching sidebar
   * `menuKey`, so the highlight stays in sync when the URL changes
   * via deep-link, browser back/forward, or programmatic navigation.
   */
  private menuFromSubState(subState: string): string {
    const key = (subState || 'dashboard').toLowerCase();
    switch (key) {
      case 'submit':                 return 'submit';
      case 'return-review':          return 'return-review';
      // Validation deep-link parity — accept both the legacy `/errors`
      // segment (ui/app/components/efile/efile.js L142-148) and the
      // friendlier `/validation` alias the migrated sidebar uses.
      case 'errors':                 return 'validation';
      case 'validation':             return 'validation';
      case 'efile-scrubbing':        return 'efile-scrubbing';
      case 'overrides':              return 'overrides';
      case 'pdf-upload':              return 'pdf-upload';
      case 'add-edit':                return 'add-edit';
      case 'efile-data':              return 'add-edit';
      case 'xml-upload':              return 'xml-upload';
      case 'delete-form':             return 'delete-form';
      case 'lock-unlock':             return 'lock-unlock';
      case 'enabled-disabled':        return 'enabled-disabled';
      case 'unlock-validation':       return 'unlock-validation';
      case 'formelements-report':     return 'formelements-report';
      case 'amendsuperseded-returns': return 'amend-superseded';
      case 'key-xml-gen':             return 'key-xml-gen';
      case 'xml-compare':             return 'xml-compare';
      case 'xml-sync':                // No sidebar entry; keep last-highlighted.
        return this.selectedMenuItem || 'dashboard';
      case 'dashboard':
      default:                        return 'dashboard';
    }
  }

  /**
   * Mirrors `vm.download8453cPdf` in
   * `ui/app/components/efile/efileElements/EfileElementsController.js` L664:
   * GET /Generate8453CPdfContent?subId=X. Per product request the 8453 PDF
   * is now opened in an adjacent browser tab (via `window.open` on a Blob
   * URL) instead of being saved to disk; the legacy filename is still set
   * on the Blob URL so browsers that "Save As" from the viewer surface
   * `8453C_<subId>.pdf` as the suggested name.
   */
  private handleDownload8453(): void {
    const subId = this.submissionId();
    if (!subId) {
      this.snackbar.openSnackBar('No submission selected for 8453 download.', 'gtw-snackbar--red');
      return;
    }
    if (this.downloading8453) return;
    this.downloading8453 = true;
    this.efileService.download8453cPdf(subId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (buffer) => {
        this.downloading8453 = false;
        this.openArrayBufferAsPdfInNewTab(buffer, `8453C_${subId}.pdf`);
      },
      error: () => {
        this.downloading8453 = false;
        this.snackbar.openSnackBar('Failed to open 8453 PDF.', 'gtw-snackbar--red');
      }
    });
  }

  /**
   * Mirrors `vm.download8879cfPdf` in EfileElementsController.js L676:
   * GET /Generate8879CPdfContent?subId=X. Per product request the 8879
   * PDF is opened in an adjacent browser tab (matching the 8453 flow)
   * instead of being saved to disk.
   */
  private handleDownload8879(): void {
    const subId = this.submissionId();
    if (!subId) {
      this.snackbar.openSnackBar('No submission selected for 8879 download.', 'gtw-snackbar--red');
      return;
    }
    if (this.downloading8879) return;
    this.downloading8879 = true;
    this.efileService.download8879cfPdf(subId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (buffer) => {
        this.downloading8879 = false;
        this.openArrayBufferAsPdfInNewTab(buffer, `8879CF_${subId}.pdf`);
      },
      error: () => {
        this.downloading8879 = false;
        this.snackbar.openSnackBar('Failed to open 8879 PDF.', 'gtw-snackbar--red');
      }
    });
  }

  private submissionId(): string | number | undefined {
    return (this.submission as any)?.SUBMISSION_ID;
  }

  /** Convert an ArrayBuffer response to a downloaded PDF (FileSaver-equivalent). */
  private saveArrayBufferAsPdf(buffer: ArrayBuffer, filename: string): void {
    const blob = new Blob([buffer], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /**
   * Open an ArrayBuffer-encoded PDF in a new browser tab so the user can
   * preview it next to the current submission instead of forcing a save
   * to disk. The Blob URL is revoked after a short delay to let the new
   * tab finish loading the document. If the popup is blocked we fall back
   * to the download path so the user still gets the PDF.
   */
  private openArrayBufferAsPdfInNewTab(buffer: ArrayBuffer, filename: string): void {
    const blob = new Blob([buffer], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');
    if (!newWindow) {
      this.snackbar.openSnackBar(
        'Popup blocked — please allow popups to view the PDF inline.',
        'gtw-snackbar--yellow',
      );
      this.saveArrayBufferAsPdf(buffer, filename);
      URL.revokeObjectURL(url);
      return;
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }
}
